import { StyleSheet, type TextStyle, type ViewStyle } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { CUSTOMIZER_SURFACE } from './characterCustomizer.constants';

/** hex (#rrggbb) → rgba string for translucent borders. */
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export const getShopCellStyles = (theme: Theme) =>
  StyleSheet.create({
    cell: {
      flex: 1,
      padding: 6,
      borderRadius: 14,
      borderWidth: 1.5,
      gap: 5,
    },
    thumb: {
      width: '100%',
      aspectRatio: 1,
      borderRadius: 11,
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
    },
    thumbGradient: {
      ...StyleSheet.absoluteFillObject,
    },
    offCircle: {
      width: 40,
      height: 40,
      borderRadius: 20,
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: theme.gray[300],
      alignItems: 'center',
      justifyContent: 'center',
    },
    offText: {
      fontSize: 9.5,
      fontWeight: '700',
      color: theme.gray[400],
    },
    rarityDot: {
      position: 'absolute',
      top: 7,
      left: 7,
      width: 8,
      height: 8,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: 'rgba(0,0,0,0.25)',
    },
    meta: {
      gap: 3,
      paddingHorizontal: 2,
      paddingBottom: 2,
    },
    name: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.black,
    },
    footerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    footerText: {
      fontSize: 10.5,
      fontWeight: '700',
    },
    pressed: {
      opacity: 0.85,
    },
  });

export type ShopCellStyles = ReturnType<typeof getShopCellStyles>;

/** Container border/background/glow vary with rarity + active state. */
export function getCellContainerStyle(
  theme: Theme,
  opts: { active: boolean; rarityColor: string | null; glow: string }
): ViewStyle {
  if (opts.active) {
    return {
      backgroundColor: CUSTOMIZER_SURFACE.cellBgActive,
      borderColor: theme.primary,
      shadowColor: opts.glow,
      shadowOpacity: 0.5,
      shadowRadius: 14,
      shadowOffset: { width: 0, height: 0 },
      elevation: 4,
    };
  }
  return {
    backgroundColor: CUSTOMIZER_SURFACE.cellBg,
    borderColor: opts.rarityColor
      ? hexToRgba(opts.rarityColor, 0.4)
      : CUSTOMIZER_SURFACE.collectionBorder,
  };
}

export function getRarityBadgeFillStyle(color: string): ViewStyle {
  return { backgroundColor: color };
}

export function getFooterTextColorStyle(color: string): TextStyle {
  return { color };
}
